import type { ComponentProps } from 'react';
import type BadgeStatus from '../../../atoms/BadgeStatus';
import { paymentMethodLabel, paymentTypeLabels } from '../../../../constants/payment';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR, toBrasiliaDateISO } from '../../../../utils/DateUtils';
import { formatTaxId } from '../../../../utils/taxIdUtils';
import {
    movementTypeColors,
    movementTypeLabels,
    statusColors,
    statusLabels,
} from '../../settlement/constants/settlement';
import type {
    TitleDebtHistory,
    TitleDebtHistoryBillingEvent,
    TitleDebtHistoryInvoice,
    TitleDebtHistoryPayment,
    TitleDebtHistoryPerson,
} from '../../settlement/services/settlementApi';

// Leitura do dossiê da cobrança para a seção Cobrança do caso (Task 16 / AF-3694). Puro e testável;
// a marcação fica no componente. As palavras são as das telas de cobrança (`settlement/constants` e
// `constants/payment`): o mesmo fato com outro nome faria o jurídico e a cobrança conversarem sobre
// coisas diferentes.

export type BadgeColor = ComponentProps<typeof BadgeStatus>['color'];

export interface BadgeView {
    text: string;
    color: BadgeColor;
}

// Espelha o STATUS_META do modal de eventos de cobrança do billing (InvoiceBillingEventsModal).
const BILLING_EVENT_STATUS: Record<string, BadgeView> = {
    PENDING: { text: 'Pendente', color: 'pending' },
    COMPLETED: { text: 'Concluído', color: 'green' },
    CANCELLED: { text: 'Cancelado', color: 'red' },
};

// O dia de um INSTANTE (payment_date, movement_date) no calendário de Brasília. Cortar a string
// daria o dia UTC, e um PIX das 22h cairia no dia seguinte.
export const dayOf = (instant: string | null | undefined): string =>
    formatDateBR(toBrasiliaDateISO(instant));

export const titleStatusBadge = (status: string): BadgeView => ({
    text: statusLabels[status] || status,
    color: statusColors[status] || 'gray',
});

export const movementBadge = (movementType: string): BadgeView => ({
    text: movementTypeLabels[movementType] || movementType,
    color: movementTypeColors[movementType] || 'gray',
});

export const billingEventBadge = (status: string): BadgeView =>
    BILLING_EVENT_STATUS[status] || { text: status, color: 'gray' };

// Concluída tem data de conclusão, pendente tem prazo; cancelada não deve mais nada.
export const billingEventWhen = (event: TitleDebtHistoryBillingEvent): string | null => {
    if (event.status === 'COMPLETED' && event.completed_at) return `concluído em ${dayOf(event.completed_at)}`;
    if (event.status === 'PENDING' && event.event_date_limit) return `prazo ${formatDateBR(event.event_date_limit)}`;
    return null;
};

// Nome com o documento formatado (CPF ou CNPJ alfanumérico), ou o que houver dos dois.
export const personLabel = (person: TitleDebtHistoryPerson | null | undefined): string | null => {
    if (!person) return null;
    const document = person.document_number ? formatTaxId(person.document_number) : null;
    if (!person.name) return document;
    return document ? `${person.name} (${document})` : person.name;
};

// A operação como a recepção a grava no título da pretensão (`_operation_reference` no backend):
// o número da operação, ou contrato-aditamento quando ela não tem número.
export const operationLabel = (history: TitleDebtHistory): string => {
    if (history.operation_number) return history.operation_number;
    if (history.sequencial_number != null) {
        return `${history.sequencial_number}-${history.amendment_number ?? 0}`;
    }
    return history.credit_operation_id || '—';
};

// "5/6" quando o título é parcela numerada; nada quando não há numeração.
export const installmentLabel = (history: TitleDebtHistory): string | null => {
    if (history.installment_number == null) return null;
    if (history.total_installments == null) return String(history.installment_number);
    return `${history.installment_number}/${history.total_installments}`;
};

export const overdueLabel = (history: TitleDebtHistory): string | null =>
    history.days_overdue > 0 ? `${history.days_overdue} dia(s) em atraso` : null;

// A fatura do movimento: o nosso número do boleto, o vencimento e o valor.
export const invoiceLabel = (invoice: TitleDebtHistoryInvoice): string =>
    [
        invoice.biller_our_number ? `Fatura nº ${invoice.biller_our_number}` : 'Fatura',
        invoice.due_date ? `venc. ${formatDateBR(invoice.due_date)}` : null,
        formatBRL(invoice.amount),
    ].filter(Boolean).join(' • ');

// O pagamento da fatura: quando, quem, por qual meio e de que natureza. A natureza importa ao
// jurídico porque confissão de dívida quita sem entrar dinheiro.
export const paymentLabel = (payment: TitleDebtHistoryPayment): string => {
    const payer = personLabel(payment.payer);
    const bank = payment.receiving_bank_account;
    return [
        payment.payment_date ? `Pago em ${dayOf(payment.payment_date)}` : 'Pago',
        payer ? `por ${payer}` : null,
        paymentMethodLabel(payment.payment_method),
        payment.payment_type ? paymentTypeLabels[payment.payment_type] || payment.payment_type : null,
        bank?.exibition_name || bank?.bank_name || null,
    ].filter(Boolean).join(' • ');
};
